import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';
import SEO from '../components/SEO';
import { companySchema, getPageMetadata, SITE } from '../config/seo';

const AboutPage = () => {
  const { language } = useLanguage();

  const getLocalizedText = (texts) => texts[language] || texts.tc;
  const meta = getPageMetadata('about', language);
  const schemas = [
    companySchema(),
    { '@type': 'AboutPage', name: meta.title, url: `${SITE.url}/about`, about: { '@id': `${SITE.url}/#organization` } },
  ];

  const history = [
    {
      period: { tc: '始於1958年 - 2000年', cn: '始于1958年 - 2000年', en: '1958 - 2000: The Beginning' },
      content: {
        tc: '始於1958年樹記海鮮有限公司的故事，當時它在中環中央街市內創立，最初以「樹記海鮮」的名義經營。多年來，樹記海鮮以提供優質產品和良好服務為宗旨，服務香港市民、餐飲業界以及輪船伙食供應等。',
        cn: '始于1958年树记海鲜有限公司的故事，当时它在中环中央街市内创立，最初以「树记海鲜」的名义经营。多年来，树记海鲜以提供优质产品和良好服务为宗旨，服务香港市民、餐饮业界以及轮船伙食供应等。',
        en: 'The story of Shu Kee Seafood Company began in 1958, when it was established in Central Market, Hong Kong, initially operating under the name "Shu Kee Seafood." Over the years, Shu Kee has committed to providing high-quality products and excellent service, catering to the residents of Hong Kong, the catering industry, and ship provisions.'
      }
    },
    {
      period: { tc: '2001年 - 2020年', cn: '2001年 - 2020年', en: '2001 - 2020: Growth and Development' },
      content: {
        tc: '2002-2020年，由於政府收回中環中央街市，樹記海鮮搬遷至西區副食品批發市場，繼續其業務。2005年，公司進行股東重組，並正式更名為「樹記海鮮有限公司」。隨著業務的發展，公司還加入了「極上之鮮」品牌，進一步提升其形象。樹記海鮮的經營理念包括堅守新鮮第一、客戶至上，並致力於提供優質專業服務。此外，公司還與多間環球海產供應商合作，確保所有海產食品來源安全可靠。',
        cn: '2002-2020年，由于政府收回中环中央街市，树记海鲜搬迁至西区副食品批发市场，继续其业务。 2005年，公司进行股东重组，并正式更名为「树记海鲜有限公司」。随着业务的发展，公司还加入了「极上之鲜」品牌，进一步提升其形象。树记海鲜的经营理念包括坚守新鲜第一、客户至上，并致力于提供优质专业服务。此外，公司还与多间环球海产供应商合作，确保所有海产食品来源安全可靠。',
        en: 'From 2002 to 2020, as the government reclaimed Central Market, Shu Kee Seafood relocated to the Western District Wholesale Market to continue its operations. In 2005, the company underwent a shareholder restructuring and officially changed its name to "Shu Kee Seafood Company Limited." With the growth of the business, the company also launched the "Supreme Fresh" brand to further enhance its image. Shu Kee\'s operating philosophy includes a steadfast commitment to freshness and customer satisfaction, with a focus on providing high-quality professional services. Additionally, the company collaborates with various global seafood suppliers to ensure the safety and reliability of all seafood products.'
      }
    },
    {
      period: { tc: '2021年 - 直至今天', cn: '2021年 - 直至今天', en: '2021 - Present: A Trusted Partner' },
      content: {
        tc: '樹記海鮮有限公司不僅是一家批發海鮮的企業，更是業界誠信與品質的典範。從當年的一家小型家庭企業發展至今，我們經歷了多次市場變革，始終秉持初心，持續提升產品與服務，成為多元市場中的佼佼者。未來我們的故事是一段關於勇氣與創新的旅程，從傳統的批發模式到如今融入科技與可持續發展的策略ESG，樹記已成為現代廚師和餐飲業界最信賴的合作夥伴。',
        cn: '树记海鲜有限公司不仅是一家批发海鲜的企业，更是业界诚信与品质的典范。从当年的一家小型家庭企业发展至今，我们经历了多次市场变革，始终秉持初心，持续提升产品与服务，成为多元市场中的佼佼者。未来我们的故事是一段关于勇气与创新的旅程，从传统的批发模式到如今融入科技与可持续发展的策略ESG，树记已成为现代厨师和餐饮业界最信赖的合作伙伴。',
        en: 'Shu Kee Seafood Company Limited has evolved from a small family business to a benchmark of integrity and quality in the industry. Over the years, we have navigated numerous market changes while remaining true to our original mission, continuously enhancing products and services to become a leader in a diverse market. Our future story is one of courage and innovation, transitioning from traditional wholesale models to incorporating technology and sustainable strategies (ESG), making Shu Kee a trusted partner for modern chefs and the catering industry.'
      }
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/about" language={language} schemas={schemas} />
      <Header />

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="about-hero">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-6 gradient-text" data-testid="about-title">
              {language === 'tc' ? '關於我們' : language === 'cn' ? '关于我们' : 'About Us'}
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {language === 'tc'
                ? '樹記海鮮有限公司不僅是一家批發海鮮的企業，更是業界誠信與品質的典範。我們的核心經營理念是堅守新鮮第一、客戶至上，並致力於提供最高標準的專業服務。'
                : language === 'cn'
                ? '树记海鲜有限公司不仅是一家批发海鲜的企业，更是业界诚信与品质的典范。我们的核心经营理念是坚守新鲜第一、客户至上，并致力于提供最高标准的专业服务。'
                : 'Shu Kee Seafood Co., Ltd. stands as a beacon of integrity and excellence in the industry. At our core, we champion the values of prioritizing freshness, delivering customer-centric service, and maintaining professionalism of the highest caliber.'}
            </p>
          </motion.div>

        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="history-section">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12" data-testid="history-title">
            {language === 'tc' ? '公司歷史' : language === 'cn' ? '公司历史' : 'Company History'}
          </h2>

          {/* History Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 max-w-4xl mx-auto"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/q6m4der6_sk_history_photo.jpeg"
                alt={language === 'tc' ? '樹記海鮮歷史' : language === 'cn' ? '树记海鲜历史' : 'Shukee Seafood History'}
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>

          <div className="space-y-12">
            {history.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row gap-8 items-start"
                data-testid={`history-item-${index}`}
              >
                <div className="md:w-1/3">
                  <div className="glass p-6 rounded-2xl">
                    <h3 className="text-2xl font-bold gradient-text">{getLocalizedText(item.period)}</h3>
                  </div>
                </div>
                <div className="md:w-2/3">
                  <div className="bg-white p-8 rounded-2xl shadow-lg border border-primary/10">
                    <p className="text-lg text-slate-700 leading-relaxed">{getLocalizedText(item.content)}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="future-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center glass p-12 rounded-3xl"
          >
            <h2 className="text-4xl font-bold mb-8 gradient-text">
              {language === 'tc' ? '展望未來' : language === 'cn' ? '展望未来' : 'Looking Ahead'}
            </h2>
            <p className="text-xl leading-relaxed text-slate-700">
              {language === 'tc'
                ? '走過六十多年，樹記海鮮有限公司一直堅持以客為本，以科技創新提升服務質素，以專業態度提升客戶滿意度，為香港餐飲業界提供最優質環球海產及食品物。未來，我們將繼續秉持這份堅持，繼續與香港同行，為香港提供新鮮可靠的優質食材品。'
                : language === 'cn'
                ? '走过六十多年，树记海鲜有限公司一直坚持以客为本，以科技创新提升服务质素，以专业态度提升客户满意度，为香港餐饮业界提供最优质环球海产及食品物。未来，我们将继续秉持这份坚持，继续与香港同行，为香港提供新鲜可靠的优质食材品。'
                : 'After more than sixty years, Shu Kee Seafood Company Limited has consistently prioritized customer satisfaction while leveraging technological innovation to enhance service quality. Our professional approach aims to maximize client satisfaction by offering the finest global seafood and food products. In the future, we will continue to uphold this commitment and partner with Hong Kong to provide fresh and reliable quality ingredients.'}
            </p>
          </motion.div>
        </div>
      </section>

      <ContactCTA />

      <Footer />
    </div>
  );
};

export default AboutPage;